import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import { Alert, Breadcrumb, Card } from "components/ui/BootstrapCompat";
import useLocalStorage from "helpers/useLocalStorage";
import { donationGetPolicy } from "services/main/donate.service";

const fallbackDonationConditionsMarkdown = `# ເງື່ອນໄຂ Donate

> ເນື້ອຫານີ້ເປັນ fallback ຊົ່ວຄາວ. ເມື່ອ backend ສົ່ງ markdown ກັບມາ ໜ້ານີ້ຈະສະແດງຂໍ້ມູນຈາກ backend ແທນ.

## ລາຍໄດ້ Donate ໄດ້ຈາກຫຍັງ

- ໄດ້ຈາກລາຍການ Donate ທີ່ຜູ້ສົນັບສະໜູນຈ່າຍຜ່ານໜ້າ Donate ຂອງທ່ານ.
- ລາຍການຕ້ອງສຳເລັດ ແລະມີສະຖານະທີ່ backend ຢືນຢັນ.
- ຍອດໃນ card ຈະອີງຈາກ summary revenue ທີ່ backend ສົ່ງກັບມາ.

## ການຂື້ນຈໍ

1. ລາຍການ Donate ເຂົ້າມາໃນລາຍການ.
2. ກົດສົ່ງຂື້ນຈໍເພື່ອສະແດງໃນ OBS/widget.
3. ລະບົບຈະບັນທຶກຈຳນວນຄັ້ງທີ່ຂື້ນຈໍ ແລະເວລາຫຼ້າສຸດ.

## ຂໍ້ຄວນຮູ້

- ຄ່າທຳນຽມ, ສະຖານະການຈ່າຍ, ແລະການຖອນເງິນ ຕ້ອງອີງຕາມ policy ຈາກ backend.
- ຖ້າລາຍການຖືກຍົກເລີກ, refund, ຫຼືການຊຳລະບໍ່ສຳເລັດ ອາດບໍ່ຖືກນັບເປັນລາຍໄດ້.
`;

const getMarkdown = (response: any) =>
  String(
    response?.data?.markdown ||
      response?.data?.contentMarkdown ||
      response?.data?.policyMarkdown ||
      response?.markdown ||
      response?.contentMarkdown ||
      response?.policyMarkdown ||
      "",
  ).trim();

const renderInlineMarkdown = (text: string) => {
  const nodes: React.ReactNode[] = [];
  const pattern = /(\*\*([^*]+)\*\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));

    if (match[2]) {
      nodes.push(
        <strong key={`${match.index}-strong`} className="font-black">
          {match[2]}
        </strong>,
      );
    } else if (match[3] && match[4]) {
      nodes.push(
        <a
          key={`${match.index}-link`}
          href={match[4]}
          target="_blank"
          rel="noreferrer"
          className="font-bold text-primary-500 underline"
        >
          {match[3]}
        </a>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

const MarkdownBlock = ({ markdown }: { markdown: string }) => {
  const lines = markdown.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];
  let orderedItems: string[] = [];

  const flushLists = () => {
    if (listItems.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="mb-3 list-disc space-y-1 pl-5">
          {listItems.map((item) => (
            <li key={item}>{renderInlineMarkdown(item)}</li>
          ))}
        </ul>,
      );
      listItems = [];
    }

    if (orderedItems.length) {
      blocks.push(
        <ol
          key={`ol-${blocks.length}`}
          className="mb-3 list-decimal space-y-1 pl-5"
        >
          {orderedItems.map((item) => (
            <li key={item}>{renderInlineMarkdown(item)}</li>
          ))}
        </ol>,
      );
      orderedItems = [];
    }
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (!line) {
      flushLists();
      return;
    }

    const unorderedMatch = line.match(/^[-*]\s+(.+)$/);
    const orderedMatch = line.match(/^\d+\.\s+(.+)$/);

    if (unorderedMatch) {
      orderedItems = [];
      listItems.push(unorderedMatch[1]);
      return;
    }

    if (orderedMatch) {
      listItems = [];
      orderedItems.push(orderedMatch[1]);
      return;
    }

    flushLists();

    if (line.startsWith("# ")) {
      blocks.push(
        <h1
          key={index}
          className="mb-2 text-[22px] font-black leading-tight text-[#101828] dark:text-slate-100"
        >
          {renderInlineMarkdown(line.replace(/^#\s+/, ""))}
        </h1>,
      );
      return;
    }

    if (line.startsWith("## ")) {
      blocks.push(
        <h2
          key={index}
          className="mb-2 mt-4 text-[16px] font-black text-[#101828] dark:text-slate-100"
        >
          {renderInlineMarkdown(line.replace(/^##\s+/, ""))}
        </h2>,
      );
      return;
    }

    if (line.startsWith("> ")) {
      blocks.push(
        <Alert key={index} variant="warning" className="mb-3">
          {renderInlineMarkdown(line.replace(/^>\s+/, ""))}
        </Alert>,
      );
      return;
    }

    blocks.push(
      <p key={index} className="mb-3">
        {renderInlineMarkdown(line)}
      </p>,
    );
  });

  flushLists();
  return (
    <div className="text-[13px] leading-6 text-[#475467] dark:text-slate-300">
      {blocks}
    </div>
  );
};

export default function DonationConditionsPage() {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const { data, isLoading, isError } = useQuery(
    ["donation-conditions-markdown", token, businessId],
    () => donationGetPolicy({ token, businessId }),
    {
      enabled: !!token,
      retry: false,
    },
  );
  const markdown = getMarkdown(data) || fallbackDonationConditionsMarkdown;

  return (
    <div className="min-w-0">
      <Breadcrumb>
        <Breadcrumb.Item active>Donate</Breadcrumb.Item>
        <Breadcrumb.Item active>ເງື່ອນໄຂ</Breadcrumb.Item>
      </Breadcrumb>

      <div className="mb-2 flex flex-col gap-2 rounded bg-white p-2 dark:bg-slate-950 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-1 text-[22px] font-black leading-tight text-[#101828] dark:text-slate-100">
            ເງື່ອນໄຂ Donate
          </h1>
          <div className="text-[12px] text-gray-600 dark:text-slate-300">
            ດຶງຂໍ້ມູນເງື່ອນໄຂແບບ markdown ຈາກ backend
          </div>
        </div>
        <Link to="/donations" className="btn btn-outline-primary">
          ກັບໄປໜ້າ Donate
        </Link>
      </div>

      {isLoading && (
        <Alert variant="info" className="mb-2">
          ກຳລັງໂຫລດເງື່ອນໄຂຈາກ backend...
        </Alert>
      )}

      {isError && (
        <Alert variant="warning" className="mb-2">
          ຍັງບໍ່ສາມາດໂຫລດ markdown ຈາກ backend ໄດ້. ຕອນນີ້ສະແດງເນື້ອຫາ
          fallback ກ່ອນ.
        </Alert>
      )}

      <Card>
        <Card.Header>Donation conditions</Card.Header>
        <Card.Body>
          <MarkdownBlock markdown={markdown} />
        </Card.Body>
      </Card>
    </div>
  );
}
